import { voiceOptions, getLanguageCode } from '../data/voiceOptions';
import { voiceQualityOptimizer, speakWithOptimization } from './voiceQualityOptimizer';

// Detect mobile Safari and other mobile browsers
export const isMobileSafari = () => {
  const userAgent = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent) && /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
};

export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Load and map available voices to options with mobile optimization
export const loadAvailableVoices = (callback) => {
  if ('speechSynthesis' in window) {
    let voices = window.speechSynthesis.getVoices();
    
    if (voices.length === 0) {
      // Wait for voices to load
      window.speechSynthesis.onvoiceschanged = () => {
        voices = window.speechSynthesis.getVoices();
        const mappedOptions = mapVoicesToOptions(voices);
        callback(mappedOptions);
      };
    } else {
      const mappedOptions = mapVoicesToOptions(voices);
      callback(mappedOptions);
    }
  } else {
    callback([]);
  }
};

// Enhanced voice mapping with mobile Safari optimization
export const mapVoicesToOptions = (voices) => {
  const mobileSafari = isMobileSafari();
  const mobile = isMobile();
  
  // Get a default English voice as ultimate fallback
  const defaultEnglishVoice = voices.find(v => v.lang.startsWith('en')) || voices[0] || null;
  
  return voiceOptions.map(option => {
    let foundVoice = null;
    
    if (mobileSafari) {
      // For mobile Safari, prioritize voices that work well
      foundVoice = findBestMobileSafariVoice(voices, option);
    } else if (mobile) {
      // For other mobile browsers, use mobile-optimized selection
      foundVoice = findBestMobileVoice(voices, option);
    } else {
      // For desktop, use the original logic
      foundVoice = findBestDesktopVoice(voices, option);
    }
    
    // If no voice found, use default English voice as fallback
    if (!foundVoice && defaultEnglishVoice) {
      foundVoice = defaultEnglishVoice;
    }
    
    return {
      ...option,
      fallbackVoice: foundVoice,
      isMobileOptimized: mobile
    };
  });
};

// Find the best voice for mobile Safari - COMPLETELY REWRITTEN for better quality
const findBestMobileSafariVoice = (voices, option) => {
  const langCode = getLanguageCode(option.countryCode);
  
  // First, try to find the exact voice name
  let foundVoice = voices.find(v => v.name === option.voiceName);
  
  // If not found, look for high-quality mobile Safari voices
  if (!foundVoice) {
    // Mobile Safari works best with these voice types - PRIORITIZE THESE
    const preferredVoiceTypes = [
      'Samantha', // High quality on iOS - FEMALE
      'Alex',     // High quality on iOS - MALE
      'Karen',    // Good quality on iOS - FEMALE
      'Daniel',   // Good quality on iOS - MALE
      'Victoria', // Good quality on iOS - FEMALE
      'Fred',     // Good quality on iOS - MALE
      'Tom',      // Good quality on iOS - MALE
      'Nicky',    // Good quality on iOS - FEMALE
      'Ralph',    // Good quality on iOS - MALE
      'Bells',    // Good quality on iOS - FEMALE
    ];
    
    // Try to find a preferred voice first
    for (const voiceType of preferredVoiceTypes) {
      foundVoice = voices.find(v => 
        v.name.includes(voiceType) && 
        v.lang.startsWith(langCode)
      );
      if (foundVoice) break;
    }
    
    // If still not found, try to find any iOS-optimized voice
    if (!foundVoice) {
      foundVoice = voices.find(v => 
        v.lang.startsWith(langCode) && 
        (v.name.includes('iOS') || v.name.includes('iPhone') || v.name.includes('iPad'))
      );
    }
    
    // If still not found, fall back to any voice with the right language and gender
    if (!foundVoice) {
      foundVoice = voices.find(v => 
        v.lang.startsWith(langCode) && 
        v.name.toLowerCase().includes(option.gender.toLowerCase())
      );
    }
    
    // Last resort: any English voice with the right gender
    if (!foundVoice) {
      foundVoice = voices.find(v => 
        v.lang.startsWith('en') && 
        v.name.toLowerCase().includes(option.gender.toLowerCase())
      );
    }
    
    // Final fallback: any English voice
    if (!foundVoice) {
      foundVoice = voices.find(v => v.lang.startsWith('en'));
    }
  }
  
  return foundVoice;
};

// Find the best voice for other mobile browsers
const findBestMobileVoice = (voices, option) => {
  const langCode = getLanguageCode(option.countryCode);
  
  // Try to find the exact voice name first
  let foundVoice = voices.find(v => v.name === option.voiceName);
  
  // If not found, try partial match
  if (!foundVoice) {
    foundVoice = voices.find(v => 
      v.name.toLowerCase().includes(option.voiceName.toLowerCase()) ||
      option.voiceName.toLowerCase().includes(v.name.toLowerCase())
    );
  }
  
  if (!foundVoice) {
    // Look for voices that work well on mobile
    foundVoice = voices.find(v => 
      v.lang.startsWith(langCode) && 
      v.name.toLowerCase().includes(option.gender.toLowerCase())
    );
    
    // If still not found, try any voice with matching language
    if (!foundVoice) {
      foundVoice = voices.find(v => v.lang.startsWith(langCode));
    }
    
    // Last resort: any English voice
    if (!foundVoice) {
      foundVoice = voices.find(v => v.lang.startsWith('en'));
    }
  }
  
  return foundVoice;
};

// Find the best voice for desktop browsers
const findBestDesktopVoice = (voices, option) => {
  // Try to find the exact voice name
  let foundVoice = voices.find(v => v.name === option.voiceName);
  
  // If not found, try partial match (voice name contains the option name)
  if (!foundVoice) {
    foundVoice = voices.find(v => 
      v.name.toLowerCase().includes(option.voiceName.toLowerCase()) ||
      option.voiceName.toLowerCase().includes(v.name.toLowerCase())
    );
  }
  
  // If still not found, try to find a similar voice by language and gender
  if (!foundVoice) {
    const langCode = getLanguageCode(option.countryCode);
    
    // Try exact language match with gender
    foundVoice = voices.find(v => 
      v.lang.startsWith(langCode) && 
      v.name.toLowerCase().includes(option.gender.toLowerCase())
    );
    
    // If still not found, try any voice with matching language
    if (!foundVoice) {
      foundVoice = voices.find(v => v.lang.startsWith(langCode));
    }
    
    // Last resort: try any English voice
    if (!foundVoice) {
      foundVoice = voices.find(v => v.lang.startsWith('en'));
    }
  }
  
  return foundVoice;
};

// Preview voice functionality with mobile optimization
export const previewVoice = (voiceOption, onStart, onEnd, onError) => {
  if (!voiceOption.fallbackVoice) {
    alert('Voice not available on this device');
    return false;
  }

  // Stop any current speech
  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance("Hello! I'm your English tutor. How can I help you today?");
  utterance.voice = voiceOption.fallbackVoice;
  
  // Mobile-optimized settings
  if (isMobile()) {
    utterance.rate = 0.8;      // Slightly slower for mobile
    utterance.pitch = 1.0;     // Normal pitch
    utterance.volume = 0.9;    // Slightly lower volume for mobile
  } else {
    utterance.rate = 0.75;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
  }

  utterance.onstart = () => {
    if (onStart) onStart(voiceOption.id);
  };

  utterance.onend = () => {
    if (onEnd) onEnd();
  };

  utterance.onerror = () => {
    if (onError) onError();
  };

  window.speechSynthesis.speak(utterance);
  return true;
};

// Get user's preferred voice
export const getPreferredVoice = () => {
  return localStorage.getItem('preferredTutorVoice');
};

// Save user's preferred voice
export const savePreferredVoice = (voiceId) => {
  localStorage.setItem('preferredTutorVoice', voiceId);
};

// Get voice object by ID
export const getVoiceById = (voiceId, availableVoices) => {
  return availableVoices.find(voice => voice.id === voiceId);
};

// COMPLETELY REWRITTEN speakText function that actually works on iPhone Safari
export const speakTextWithPreferredVoice = (text, availableVoices, onStart, onEnd, onError) => {
  if ('speechSynthesis' in window) {
    // Stop any current speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    
    // Get preferred voice - THIS IS THE KEY FIX
    const preferredVoiceId = getPreferredVoice();
    if (preferredVoiceId && availableVoices) {
      const preferredVoice = getVoiceById(preferredVoiceId, availableVoices);
      if (preferredVoice && preferredVoice.fallbackVoice) {
        utterance.voice = preferredVoice.fallbackVoice;
        console.log('Using selected voice:', preferredVoice.fallbackVoice.name);
      }
    }
    
    // Mobile-optimized settings
    if (isMobileSafari()) {
      // iOS Safari specific optimizations - COMPLETELY REWRITTEN
      utterance.lang = 'en-US';
      utterance.rate = 0.8;     // Balanced rate for clarity
      utterance.pitch = 1.0;    // Normal pitch
      utterance.volume = 0.9;   // Good volume for iOS
      
      // iOS Safari works better with these settings
      utterance.text = text.trim(); // Ensure clean text
      
      // Additional iOS Safari optimizations
      if (text.length > 100) {
        // For longer text, use slightly slower rate
        utterance.rate = 0.75;
      }
    } else if (isMobile()) {
      // Other mobile browsers
      utterance.lang = 'en-US';
      utterance.rate = 0.8;
      utterance.pitch = 1.0;
      utterance.volume = 0.9;
    } else {
      // Desktop browsers
      utterance.lang = 'en-US';
      utterance.rate = 0.75;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
    }
    
    // Set up event handlers based on device type
    if (isMobileSafari()) {
      utterance.onstart = () => {
        if (onStart) onStart(text);
        // iOS Safari sometimes needs a resume after start
        setTimeout(() => {
          if (window.speechSynthesis.paused) {
            window.speechSynthesis.resume();
          }
        }, 100);
      };
    } else {
      utterance.onstart = () => {
        if (onStart) onStart(text);
      };
    }
    
    utterance.onend = () => {
      if (onEnd) onEnd();
    };
    
    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      if (onError) onError(event);
    };
    
    // For mobile Safari, add a small delay to ensure proper initialization
    if (isMobileSafari()) {
      setTimeout(() => {
        // iOS Safari workaround: pause/resume before speaking
        window.speechSynthesis.pause();
        window.speechSynthesis.resume();
        window.speechSynthesis.speak(utterance);
      }, 100); // Reduced delay for better responsiveness
    } else {
      window.speechSynthesis.speak(utterance);
    }
  }
};

// REWRITTEN fallback function that actually respects voice selection
export const speakTextWithFallback = (text, availableVoices, onStart, onEnd, onError) => {
  // FIRST: Try with the user's preferred voice (this was missing!)
  const preferredVoiceId = getPreferredVoice();
  if (preferredVoiceId && availableVoices) {
    const preferredVoice = getVoiceById(preferredVoiceId, availableVoices);
    if (preferredVoice && preferredVoice.fallbackVoice) {
      console.log('Using preferred voice in fallback:', preferredVoice.fallbackVoice.name);
      speakTextWithPreferredVoice(text, availableVoices, onStart, onEnd, onError);
      return;
    }
  }
  
  // SECOND: Try the voice quality optimizer
  try {
    speakWithOptimization(text, availableVoices, onStart, onEnd, onError);
  } catch (error) {
    console.warn('Optimized TTS failed, trying basic fallback:', error);
    
    // THIRD: Fallback: try with default voice and basic settings
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.7;  // Even slower for fallback
      utterance.pitch = 1.0;
      utterance.volume = 0.8;
      
      utterance.onstart = () => {
        if (onStart) onStart(text);
      };
      
      utterance.onend = () => {
        if (onEnd) onEnd();
      };
      
      utterance.onerror = (event) => {
        console.error('Fallback TTS also failed:', event);
        if (onError) onError(event);
      };
      
      window.speechSynthesis.speak(utterance);
    }
  }
};

// New function that provides the best possible voice quality
export const speakTextWithBestQuality = (text, availableVoices, onStart, onEnd, onError) => {
  // Get optimization recommendations for debugging
  const recommendations = voiceQualityOptimizer.getOptimizationRecommendations();
  console.log('Voice optimization recommendations:', recommendations);
  
  // Use the optimizer for the best quality
  return speakWithOptimization(text, availableVoices, onStart, onEnd, onError);
};
